<script setup lang="ts">
import { computed, ref } from 'vue';
import { toJpeg } from 'html-to-image';
import { UploadCloud, Loader2, Trash2 } from 'lucide-vue-next';
import { api } from '@/composables/useApi';

/**
 * Official referee card — passport-style accreditation in a
 * business-card form factor. Used in:
 *   - Admin referee profile (with upload / replace / delete controls)
 *   - Referee dashboard (read-only, with "Download my card")
 *
 * The card now mirrors the federation roster fields directly so it
 * doesn't duplicate the side-panel "Details" block: phone, status,
 * grade and current-season membership. The status flag also makes
 * "INACTIVE" / "SUSPENDED" visible at a glance in printed copies.
 */

type RefereeStatus = 'active' | 'inactive' | 'suspended' | 'diaspora';

interface Referee {
    id: string;
    first_name?: string;
    last_name?: string;
    name?: string;
    license_id?: string;
    grade?: string;
    level_rank?: 'Beginner' | 'Regional' | 'National' | 'International' | '' | string;
    country?: string;
    phone?: string;
    email?: string;
    /** Legacy boolean kept for backwards compatibility; new code uses `status`. */
    active?: boolean;
    /** New canonical field: 'active' | 'inactive' | 'suspended'. */
    status?: RefereeStatus;
    has_photo?: boolean;
    /** Public R2 URL of the referee's photo (empty / undefined = no photo). */
    photo_url?: string;
    photo_key?: string;
    photo_mime?: string;
}

const props = defineProps<{
    referee: Referee;
    /** Render upload / delete affordances (admin view). */
    editable?: boolean;
    /** Federation label rendered on the brand strip. */
    organization?: string;
    /** Optional: name of the current season shown on the card so it's
     *  obvious whether the referee is active for the running campaign. */
    seasonName?: string;
    /** True when the referee is rostered for the current season. When
     *  undefined we fall back to assuming yes unless status says otherwise. */
    inSeason?: boolean;
}>();
const emit = defineEmits<{ (e: 'photo-changed'): void }>();

const uploading = ref(false);
const isDragging = ref(false);
const error = ref<string | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);

// Photo is served straight from R2 via its public URL — no auth'd
// fetch needed. The parent reloads the referee record after upload
// so this reactively updates.
const photoUrl = computed(() => props.referee.photo_url || null);

/** Root card element + capture flag. Used by the JPEG export to
 *  hide upload/download controls during rasterisation so the saved
 *  image is clean — exactly the printed card, nothing else. */
const cardEl = ref<HTMLElement | null>(null);
const exporting = ref(false);
const jpegBusy = ref(false);

const fullName = computed(() => {
    const r = props.referee;
    const last = (r.last_name || '').toUpperCase();
    return [r.first_name, last].filter(Boolean).join(' ') || r.name || '—';
});
const country = computed(() => prettyCountry(props.referee.country || ''));
const orgName = computed(() => props.organization || 'ARAVO');

/**
 * Resolve a single status value from either the new `status` field
 * or the legacy `active` boolean. Defaults to `active` so historical
 * rows that haven't been migrated still print correctly.
 */
const status = computed<RefereeStatus>(() => {
    const s = (props.referee.status as RefereeStatus | undefined);
    if (s === 'active' || s === 'inactive' || s === 'suspended' || s === 'diaspora') return s;
    return props.referee.active === false ? 'inactive' : 'active';
});
const statusLabel = computed(() => ({
    active:    'Active',
    inactive:  'Inactive',
    suspended: 'Suspended',
    diaspora:  'Diaspora',
} as const)[status.value]);

const grade = computed(() => {
    const g = (props.referee.level_rank || '').trim();
    if (g && ['Beginner', 'Regional', 'National', 'International', 'International Candidate', 'FIVB International'].includes(g)) return g;
    // Fall back to the legacy `grade` (Local / International).
    return (props.referee.grade || '—');
});
const phone = computed(() => (props.referee.phone || '').trim() || '—');

const isInSeason = computed(() => {
    if (typeof props.inSeason === 'boolean') return props.inSeason;
    // No explicit flag yet — derive from status: anyone not Active
    // is treated as out of season for the current campaign.
    return status.value === 'active';
});

async function onFile(ev: Event) {
    const input = ev.target as HTMLInputElement;
    await uploadFile(input.files?.[0] ?? null);
    input.value = '';     // allow re-pick of the same file
}

async function onDrop(ev: DragEvent) {
    isDragging.value = false;
    if (uploading.value) return;
    await uploadFile(ev.dataTransfer?.files?.[0] ?? null);
}

async function uploadFile(f: File | null) {
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(f.type)) {
        error.value = 'Unsupported image type (JPEG, PNG, WebP or GIF)';
        return;
    }
    if (f.size > 5 * 1024 * 1024) {
        error.value = 'Image too large (max 5 MB)';
        return;
    }
    error.value = null;
    uploading.value = true;
    try {
        // Single round-trip: backend reads the file, uploads to R2,
        // and saves the resulting public URL on the referee doc.
        const fd = new FormData();
        fd.append('file', f);
        await api(`/referees/${encodeURIComponent(props.referee.id)}/photo`, {
            method: 'POST',
            body: fd,
        });
        emit('photo-changed');
    } catch (e) {
        error.value = e instanceof Error ? e.message : String(e);
    } finally {
        uploading.value = false;
    }
}

async function removePhoto() {
    if (!confirm('Remove the photo?')) return;
    try {
        await api(`/referees/${encodeURIComponent(props.referee.id)}/photo`, { method: 'DELETE' });
        emit('photo-changed');
    } catch (e) {
        error.value = e instanceof Error ? e.message : String(e);
    }
}

function downloadPdf() {
    const a = document.createElement('a');
    a.href = `/api/pdf/referee-card?referee_id=${encodeURIComponent(props.referee.id)}`;
    a.click();
}

/**
 * Export the card as a high-resolution JPEG.
 *
 * html-to-image crops when it captures *before* fonts/images have
 * finished loading, because the bounding-box measurement runs early
 * and the layout shifts after. We defend against that with three
 * things:
 *
 *   1. Wait for `document.fonts.ready` so the Google-fonts stack is
 *      fully resolved.
 *   2. Wait for every <img> inside the card to report `complete`
 *      (with a 2s safety timeout per image).
 *   3. Pass an explicit width + height from getBoundingClientRect()
 *      so the captured canvas is exactly the card's real size — no
 *      relying on html-to-image's internal measurement.
 *
 * A two-rAF gap after toggling `is-exporting` makes sure the toolbar
 * controls have actually been laid out as `visibility: hidden` before
 * the capture pass starts. Captured at 3× pixel ratio for crisp
 * retina + print output.
 */
async function downloadJpeg() {
    if (!cardEl.value || jpegBusy.value) return;
    jpegBusy.value = true;
    exporting.value = true;
    error.value = null;
    let restoreImages: (() => void) | null = null;
    try {
        // 1. Fonts. Inter / Manrope arrive after first paint when the
        // page just loaded; without this wait the card captures in
        // the system fallback face and the width can shift.
        const fonts = (document as any).fonts;
        if (fonts?.ready) { try { await fonts.ready; } catch { /* ignore */ } }

        // 2. Images. Inline the ARAVO logo + referee photo as data URLs
        // up-front so html-to-image never has to fetch them during the
        // capture. This is what makes the photo reliably appear in the
        // downloaded card on phones (mobile browsers drop the library's
        // own cross-origin image fetch). inlineImages also waits for the
        // swapped-in images to load + decode before we continue.
        restoreImages = await inlineImages(cardEl.value);

        // 3. Let Vue + the browser flush the visibility:hidden toggle
        // AND the forced is-exporting layout (mobile users get pushed
        // back to the 560 px desktop layout for the capture).
        await new Promise(requestAnimationFrame);
        await new Promise(requestAnimationFrame);

        // html-to-image tries to inline web-font @font-face rules by
        // reading `cssRules` off every <link rel=stylesheet>. The
        // Google Fonts stylesheet is cross-origin so the browser
        // throws SecurityError. Fetch the CSS ourselves (allowed via
        // fetch() because Google sends CORS headers) and feed it to
        // the library via fontEmbedCSS — bypasses the cssRules read.
        const fontEmbedCSS = await fetchFontEmbedCSS();

        const rect = cardEl.value.getBoundingClientRect();
        // Lower the pixel ratio on phones: iOS Safari silently returns a
        // blank image when the canvas gets too big. 2× is still crisp.
        const isMobile = Math.min(window.innerWidth, window.innerHeight) < 600
            || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
        const opts = {
            quality: 0.95,
            pixelRatio: isMobile ? 2 : 3,
            backgroundColor: '#ffffff',
            // Don't enable cacheBust — in html-to-image v1.11.x it mutates
            // the live <img src> with ?<timestamp>, leaking the query
            // string into the displayed URL. We don't need it: every
            // photo upload gets a fresh random R2 key, so the browser
            // never sees a stale-cache collision.
            // If the CSS fetch failed, skipFonts keeps the export working
            // (system fallback fonts) instead of crashing on cssRules.
            ...(fontEmbedCSS ? { fontEmbedCSS } : { skipFonts: true }),
            width:  Math.ceil(rect.width),
            height: Math.ceil(rect.height),
            // Re-anchor the captured node so any inherited transform
            // from a parent (e.g. zoom utilities) doesn't shrink it.
            style: { transform: 'none', margin: '0' },
            filter: (node: HTMLElement) =>
                !(node.dataset && node.dataset.noExport === 'true'),
        };

        // Safari / iOS drops images on the *first* html-to-image pass
        // (its internal resource cache isn't warm yet). A throwaway
        // warm-up render makes the real capture reliable everywhere.
        try { await toJpeg(cardEl.value, opts); } catch { /* warm-up */ }
        const dataUrl = await toJpeg(cardEl.value, opts);

        const slug = (props.referee.last_name || props.referee.name || props.referee.id || 'referee')
            .toLowerCase().replace(/[^a-z0-9]+/g, '-');
        await saveOrShare(dataUrl, `aravo-referee-card-${slug}.jpg`);
    } catch (e) {
        error.value = 'Could not export JPEG: ' + (e instanceof Error ? e.message : String(e));
    } finally {
        restoreImages?.();      // put the live <img> srcs back
        exporting.value = false;
        jpegBusy.value = false;
    }
}

/**
 * Pull every Google Fonts stylesheet linked on the page and return
 * the concatenated CSS as a string. We can read these via fetch()
 * (Google sends Access-Control-Allow-Origin: *) but the browser
 * won't let JS read .cssRules off them once they're attached as
 * <link rel=stylesheet>. Cached on the module so we only fetch once.
 */
/**
 * Replace every <img> src inside `root` with a self-contained base64
 * data URL, returning a function that restores the originals.
 *
 * This is the reliable cross-device fix for "the photo doesn't appear
 * in the downloaded card on phones". html-to-image normally fetches and
 * inlines remote images itself, but on mobile Safari/Chrome that fetch
 * is flaky (timing + cross-origin) and the referee photo silently drops
 * out. By fetching the bytes ourselves and putting a `data:` URL into
 * the live DOM *before* the capture, the library only has to copy an
 * already-embedded image — no network, no taint, works everywhere.
 */
async function inlineImages(root: HTMLElement): Promise<() => void> {
    const imgs = Array.from(root.querySelectorAll('img'));
    const restores: Array<() => void> = [];
    await Promise.all(imgs.map(async (img) => {
        const src = img.getAttribute('src') || '';
        if (!src || src.startsWith('data:')) return;
        try {
            const res = await fetch(src, { mode: 'cors', cache: 'force-cache' });
            if (!res.ok) return;
            const blob = await res.blob();
            const dataUrl = await new Promise<string>((resolve, reject) => {
                const fr = new FileReader();
                fr.onload = () => resolve(fr.result as string);
                fr.onerror = reject;
                fr.readAsDataURL(blob);
            });
            const original = img.getAttribute('src');
            img.setAttribute('src', dataUrl);
            restores.push(() => { if (original !== null) img.setAttribute('src', original); });
            // Wait for the swapped-in data URL to be painted + decoded.
            if (!(img.complete && img.naturalWidth > 0)) {
                await new Promise<void>(r => {
                    img.addEventListener('load',  () => r(), { once: true });
                    img.addEventListener('error', () => r(), { once: true });
                    setTimeout(r, 5000);
                });
            }
            try { if (typeof img.decode === 'function') await img.decode(); } catch { /* broken img → fine */ }
        } catch { /* leave the original src in place if we can't fetch it */ }
    }));
    return () => restores.forEach(fn => fn());
}

/**
 * Deliver the rendered JPEG to the user, cross-device.
 *
 * The old `a.download` of a data URL works on desktop but iOS Safari
 * ignores the `download` attribute and just opens the image in the same
 * tab — so on a phone "Download JPEG" appeared to do nothing. We now:
 *   • On mobile (where the Web Share API accepts files) hand the JPEG to
 *     the native share sheet — the reliable "Save to Photos / Files"
 *     path, with the photo intact, identical to what a laptop saves.
 *   • Everywhere else, download via a blob object URL (not the raw data
 *     URL, which some browsers cap in length).
 */
async function saveOrShare(dataUrl: string, filename: string): Promise<void> {
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], filename, { type: 'image/jpeg' });

    // Only route through the OS share sheet on phones/tablets (where the
    // plain download is unreliable). Desktop keeps its direct download.
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '')
        || (Math.min(window.innerWidth, window.innerHeight) < 600 && 'ontouchstart' in window);
    const nav = navigator as any;
    if (isMobile && typeof nav.canShare === 'function' && nav.canShare({ files: [file] })) {
        try {
            await nav.share({ files: [file], title: filename });
            return;                              // saved/shared via the OS sheet
        } catch (e: any) {
            if (e?.name === 'AbortError') return;   // user dismissed — done
            // any other share failure → fall through to a normal download
        }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
}

let cachedFontCss: string | null = null;
async function fetchFontEmbedCSS(): Promise<string | undefined> {
    if (cachedFontCss !== null) return cachedFontCss || undefined;
    try {
        const links = Array.from(document.querySelectorAll<HTMLLinkElement>(
            'link[rel="stylesheet"][href*="fonts.googleapis.com"]'
        ));
        const parts = await Promise.all(links.map(async (link) => {
            const r = await fetch(link.href);
            return r.ok ? r.text() : '';
        }));
        cachedFontCss = parts.join('\n').trim();
        return cachedFontCss || undefined;
    } catch {
        cachedFontCss = '';
        return undefined;
    }
}

function prettyCountry(code: string): string {
    const map: Record<string, string> = {
        RWA: 'Rwanda', KEN: 'Kenya', UGA: 'Uganda', TZA: 'Tanzania',
        BDI: 'Burundi', COD: 'DR Congo', ETH: 'Ethiopia',
    };
    return map[code.toUpperCase()] || code || '—';
}
</script>

<template>
    <div class="ref-card" :data-status="status" :class="exporting ? 'is-exporting' : ''" ref="cardEl">
        <!-- ════════ Brand band — federation logo + full name in bold ═══ -->
        <header class="ref-card-head">
            <div class="ref-card-head-brand">
                <span class="ref-card-logo">
                    <img src="/aravo-logo.jpg" alt="ARAVO" />
                </span>
                <div class="ref-card-head-text">
                    <div class="ref-card-org">
                        Association Rwandaise des Arbitres de Volleyball
                    </div>
                    <div class="ref-card-tag">Official Referee Card</div>
                </div>
            </div>
            <div class="ref-card-dl-group" data-no-export="true">
                <button v-if="editable || true" @click="downloadJpeg" :disabled="jpegBusy"
                        class="ref-card-dl primary" type="button">
                    {{ jpegBusy ? 'Preparing…' : 'Download JPEG' }}
                </button>
                <button @click="downloadPdf" class="ref-card-dl" type="button">
                    PDF
                </button>
            </div>
        </header>

        <!-- ════════ Body: photo + identity grid ════════════════════════════ -->
        <div class="ref-card-body">
            <!-- Watermark print stamp — only visible when status isn't active.
                 Forces the federation eye to see immediately that this
                 person can't officiate right now. -->
            <div v-if="status === 'inactive' || status === 'suspended'" class="ref-card-stamp" :class="'is-' + status">
                {{ statusLabel.toUpperCase() }}
            </div>

            <!-- Photo column — single upload target.
                 The photo frame IS the dropzone in admin mode:
                   • Click the frame  → opens the OS file picker
                   • Drag-and-drop    → accepts an image straight onto it
                   • Photo present    → click still re-opens picker to replace
                                        + a small "Remove" button sits below
                 No separate dashed area underneath any more. -->
            <div class="ref-card-photo-col">
                <component :is="editable ? 'label' : 'div'"
                           class="ref-card-photo-frame"
                           :class="editable ? 'is-editable' : ''"
                           :data-state="editable ? (uploading ? 'uploading' : (isDragging ? 'dragging' : (photoUrl ? 'has-photo' : 'empty'))) : null"
                           @dragover.prevent="editable && (isDragging = true)"
                           @dragenter.prevent="editable && (isDragging = true)"
                           @dragleave.prevent="editable && (isDragging = false)"
                           @drop="editable ? onDrop($event) : null">
                    <img v-if="photoUrl" :src="photoUrl" crossorigin="anonymous"
                         alt="referee photo" class="ref-card-photo-img" />
                    <div v-else class="ref-card-photo-empty">
                        <template v-if="editable">
                            <Loader2 v-if="uploading" class="w-6 h-6 animate-spin" />
                            <UploadCloud v-else class="w-6 h-6" />
                            <span class="ref-card-photo-cta">
                                {{ uploading
                                    ? 'Uploading…'
                                    : isDragging
                                        ? 'Drop image'
                                        : 'Click or drop photo' }}
                            </span>
                            <span class="ref-card-photo-hint">JPEG · PNG · WebP · GIF · 5 MB</span>
                        </template>
                        <template v-else>
                            <span class="ref-card-photo-empty-text">No photo<br>uploaded</span>
                        </template>
                    </div>
                    <!-- Hover overlay on top of an existing photo — invites
                         a click/drop to replace. Only on admin view. -->
                    <div v-if="editable && photoUrl" class="ref-card-photo-overlay" data-no-export="true">
                        <Loader2 v-if="uploading" class="w-5 h-5 animate-spin" />
                        <UploadCloud v-else class="w-5 h-5" />
                        <span>{{ uploading ? 'Uploading…' : isDragging ? 'Drop to replace' : 'Click or drop to replace' }}</span>
                    </div>
                    <input v-if="editable" ref="fileInput" type="file"
                           accept="image/jpeg,image/png,image/webp,image/gif"
                           @change="onFile" :disabled="uploading" class="sr-only"
                           data-no-export="true" />
                </component>

                <div v-if="editable && (photoUrl || error)"
                     class="ref-card-photo-meta" data-no-export="true">
                    <button v-if="photoUrl && !uploading"
                            @click="removePhoto" type="button"
                            class="ref-card-photo-remove">
                        <Trash2 class="w-3.5 h-3.5" />
                        Remove photo
                    </button>
                    <p v-if="error" class="ref-card-photo-err">{{ error }}</p>
                </div>
            </div>

            <!-- Identity column -->
            <div class="ref-card-id-col">
                <div class="ref-card-name-box">
                    <div class="ref-card-name-label">Name</div>
                    <div class="ref-card-name">{{ fullName }}</div>
                </div>

                <!-- Identity grid — two equal columns. Pairs read top
                     to bottom: Phone/Country, Position/Grade, Season/Status.
                     Each cell carries its own box with a faint mint
                     background so the grid reads as separated chips. -->
                <dl class="ref-card-grid">
                    <div class="ref-card-cell">
                        <dt>Phone</dt>
                        <dd class="ref-card-value font-mono">{{ phone }}</dd>
                    </div>
                    <div class="ref-card-cell">
                        <dt>Country</dt>
                        <dd class="ref-card-value">{{ country }}</dd>
                    </div>
                    <div class="ref-card-cell ref-card-cell-position">
                        <dt>Position</dt>
                        <dd class="ref-card-position-value">Referee</dd>
                    </div>
                    <div class="ref-card-cell">
                        <dt>Level</dt>
                        <dd class="ref-card-value">{{ grade }}</dd>
                    </div>
                    <!-- Status sits on its own row, centred, so it
                         reads as the federation summary line under
                         the identity block. The Season cell was
                         removed — status is the single source of
                         truth for whether the referee can officiate. -->
                    <div class="ref-card-cell ref-card-cell-wide ref-card-cell-status">
                        <dt>Status</dt>
                        <dd>
                            <span class="ref-card-status-pill" :class="'is-' + status">
                                {{ statusLabel }}
                            </span>
                        </dd>
                    </div>
                </dl>
            </div>
        </div>

        <!-- ════════ Foot: paired federation marks + ownership note ═══ -->
        <footer class="ref-card-foot">
            <div class="ref-card-marks">
                <span class="ref-card-mark ref-card-mark-frvb">FRVB</span>
                <span class="ref-card-mark ref-card-mark-aravo">ARAVO</span>
            </div>
            <span class="ref-card-foot-note">
                This card remains the property of the federation.
            </span>
        </footer>
    </div>
</template>

<style scoped>
/* ════════════════════════════════════════════════════════════════════
   Business-card aesthetic — landscape layout with a deep emerald
   brand band, yellow accent line, and a two-column body (photo /
   identity). Designed to read clearly on screen and to print
   exactly the same way (no fancy backgrounds that ink would hate).
═══════════════════════════════════════════════════════════════════════ */
.ref-card {
    position: relative;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 14px;
    overflow: hidden;
    box-shadow:
        0 16px 32px -20px rgba(15, 23, 42, 0.35),
        0 4px 10px -6px rgba(15, 23, 42, 0.18);
    font-family: var(--font-sans, "Inter", sans-serif);
    color: #0f172a;
    max-width: 560px;
    margin: 0 auto;
    width: 100%;
}

/* Brand strip — deep emerald with federation yellow underline. */
.ref-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 14px 18px 12px;
    color: #ffffff;
    background:
        radial-gradient(circle at 18% 20%, rgba(110, 231, 183, 0.22), transparent 60%),
        linear-gradient(120deg, #022C22 0%, #064E3B 60%, #065F46 100%);
    border-bottom: 3px solid #FACC15;
}
.ref-card-head-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1 1 auto;
}
.ref-card-logo {
    flex-shrink: 0;
    width: 46px; height: 46px;
    background: #ffffff;
    border: 2px solid #FACC15;
    border-radius: 8px;
    padding: 3px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.4);
}
.ref-card-logo img { width: 100%; height: 100%; object-fit: contain; display: block; }

.ref-card-head-text { line-height: 1.2; min-width: 0; }
.ref-card-org {
    font-family: "Manrope", "Inter", sans-serif;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: 0.01em;
    color: #ffffff;
    line-height: 1.2;
    word-break: break-word;
}
.ref-card-tag {
    font-size: 13px; font-weight: 800;
    color: #EAB308;                /* rich gold — reads clearly on the green band */
    text-transform: uppercase; letter-spacing: 0.14em;
    margin-top: 4px;
}
.ref-card-dl-group {
    display: flex; align-items: center; gap: 6px;
    flex-shrink: 0;
}
.ref-card-dl {
    background: rgba(255, 255, 255, 0.14);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 8px;
    padding: 7px 11px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition: background 120ms, border-color 120ms;
    white-space: nowrap;
}
.ref-card-dl:hover { background: rgba(255, 255, 255, 0.24); border-color: rgba(255, 255, 255, 0.45); }
.ref-card-dl:disabled { opacity: 0.55; cursor: wait; }
.ref-card-dl.primary {
    background: linear-gradient(135deg, #FACC15 0%, #EAB308 100%);
    color: #022C22;
    border-color: rgba(202, 138, 4, 0.7);
}
.ref-card-dl.primary:hover { background: linear-gradient(135deg, #EAB308 0%, #CA8A04 100%); }

/* Body — two columns at desktop, stacks at < 480px. */
.ref-card-body {
    position: relative;
    display: grid;
    grid-template-columns: 154px 1fr;
    gap: 18px;
    padding: 18px;
    background:
        repeating-linear-gradient(135deg, transparent 0 12px, rgba(2, 44, 34, 0.02) 12px 13px);
    /* Subtle diagonal stripe acts as the "security paper" texture
       common on corporate IDs — invisible but adds depth. */
}
@media (max-width: 480px) {
    .ref-card-body { grid-template-columns: 1fr; }
}

/* Watermark stamp when the referee can't officiate. Bold, rotated,
   sits across the card so a printed copy can't be mistaken for valid. */
.ref-card-stamp {
    position: absolute;
    top: 38%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-15deg);
    font-family: "Manrope", "Inter", sans-serif;
    font-weight: 800;
    font-size: 56px;
    letter-spacing: 0.12em;
    padding: 6px 22px;
    border: 4px solid;
    border-radius: 10px;
    opacity: 0.18;
    pointer-events: none;
    z-index: 1;
    text-transform: uppercase;
    white-space: nowrap;
}
.ref-card-stamp.is-inactive  { color: #475569; border-color: #475569; }
.ref-card-stamp.is-suspended { color: #b91c1c; border-color: #b91c1c; opacity: 0.22; }

/* Photo block */
.ref-card-photo-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}
/* The photo frame is the SINGLE upload affordance for admins.
   Clicking opens the file picker (it's a <label> wrapping a hidden
   <input type="file">); dragging an image over it switches the
   border to a brighter teal "drop here" state; a hover overlay
   appears when a photo already exists. No secondary dropzone. */
.ref-card-photo-frame {
    position: relative;
    width: 138px;
    height: 168px;
    background: #f8fafc;
    border: 3px solid #FACC15;     /* federation yellow accent */
    border-radius: 6px;
    overflow: hidden;
    box-shadow:
        0 6px 14px -8px rgba(15, 23, 42, 0.35),
        inset 0 0 0 1px rgba(255, 255, 255, 0.6);
    display: flex; align-items: center; justify-content: center;
    transition: border-color 120ms, box-shadow 120ms, background 120ms;
}
.ref-card-photo-frame.is-editable {
    cursor: pointer;
}
.ref-card-photo-frame.is-editable:hover {
    border-color: #0d9488;
    box-shadow:
        0 10px 18px -8px rgba(15, 118, 110, 0.45),
        inset 0 0 0 1px rgba(255, 255, 255, 0.6);
}
.ref-card-photo-frame[data-state="dragging"] {
    border-color: #14b8a6 !important;
    background: #ecfdf5;
    box-shadow:
        0 0 0 3px rgba(20, 184, 166, 0.25),
        inset 0 0 0 1px rgba(255, 255, 255, 0.6);
}
.ref-card-photo-frame[data-state="uploading"] {
    border-color: #0d9488;
    cursor: wait;
}
.ref-card-photo-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ref-card-photo-empty {
    color: #64748b; font-size: 11px; line-height: 1.4;
    text-align: center; padding: 0 10px;
    display: flex; flex-direction: column; align-items: center;
    gap: 6px;
}
.ref-card-photo-empty-text { color: #94a3b8; font-style: italic; }
.ref-card-photo-cta {
    font-weight: 700;
    color: #0f766e;
    letter-spacing: 0.01em;
    font-size: 11.5px;
}
.ref-card-photo-hint {
    font-size: 9.5px;
    color: #94a3b8;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

/* Hover overlay sits on top of an existing photo, prompting the
   admin to click/drop to replace. Subtle gradient so the photo
   stays partly visible. */
.ref-card-photo-overlay {
    position: absolute; inset: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 4px;
    color: #ffffff;
    font-size: 10.5px; font-weight: 700;
    text-align: center; padding: 0 8px;
    background: linear-gradient(180deg, rgba(2, 44, 34, 0.55), rgba(2, 44, 34, 0.75));
    opacity: 0;
    transition: opacity 120ms;
    pointer-events: none;
}
.ref-card-photo-frame.is-editable:hover .ref-card-photo-overlay,
.ref-card-photo-frame[data-state="dragging"] .ref-card-photo-overlay,
.ref-card-photo-frame[data-state="uploading"] .ref-card-photo-overlay {
    opacity: 1;
}

/* Below the frame: remove button + any inline error. */
.ref-card-photo-meta {
    margin-top: 8px;
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    width: 100%;
}
.ref-card-photo-remove {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 4px 9px;
    border-radius: 4px;
    border: 1px solid #fca5a5;
    background: #ffffff;
    color: #b91c1c;
    font-size: 10.5px;
    font-weight: 700;
    cursor: pointer;
    transition: background 120ms;
}
.ref-card-photo-remove:hover { background: #fef2f2; }
.ref-card-photo-err {
    margin: 0;
    border: 1px solid #fecaca;
    background: #fef2f2;
    color: #b91c1c;
    padding: 4px 7px;
    border-radius: 4px;
    font-size: 10.5px;
    font-weight: 600;
    text-align: center;
}

/* Identity column */
.ref-card-id-col {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
}
.ref-card-name-box {
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 8px;
    padding: 8px 12px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}
.ref-card-name-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #64748b;
}
.ref-card-name {
    font-family: "Manrope", "Inter", sans-serif;
    font-size: 18px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: 0.01em;
    margin-top: 2px;
    line-height: 1.2;
    word-break: break-word;
}

.ref-card-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin: 0;
}
/* Each cell — subtle mint background tints the whole identity
   grid green so the card reads as the federation's brand surface.
   Border is a darker mint so the cells still visually separate
   from each other on the mint backdrop. */
.ref-card-cell {
    border: 1px solid #a7f3d0;          /* emerald-200 */
    border-radius: 8px;
    padding: 7px 10px;
    background: #ecfdf5;                /* emerald-50, faint mint wash */
    min-width: 0;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}
.ref-card-cell-wide { grid-column: 1 / -1; }
.ref-card-cell dt {
    font-family: "Inter", sans-serif;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #0f766e;                     /* teal-700 — matches the mint */
}
.ref-card-cell dd {
    margin: 2px 0 0;
    font-family: "Inter", sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #022C22;
    /* Plain words shouldn't break mid-character on narrow widths —
       only fall back to breaking when there's no other option. */
    overflow-wrap: break-word;
    word-break: normal;
}
/* Manrope display for values that read as identity headlines
   (Country, Grade, etc.). Kept slightly larger and tighter so the
   typography hierarchy stays distinct from the small label text. */
.ref-card-value {
    font-family: "Manrope", "Inter", sans-serif;
    font-size: 13.5px;
    font-weight: 700;
    letter-spacing: 0.005em;
}

/* Position cell — role on the federation card. Smaller now that
   it sits in a half-width box; still rendered in Manrope-Bold +
   federation forest-green to read as the role headline. */
.ref-card-cell-position { padding: 7px 10px; }

/* Status row — full-width, centred label + pill. Sits on its own
   line as the closing summary of the identity grid. */
.ref-card-cell-status { text-align: center; padding: 8px 10px; }
.ref-card-cell-status dt { letter-spacing: 0.18em; }
.ref-card-cell-status dd { margin-top: 4px; }
.ref-card-position-value {
    font-family: "Manrope", "Inter", sans-serif;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.02em;
    color: #064E3B;
    line-height: 1.15;
}

/* Status pill colours.
   `white-space: nowrap` so labels like "Suspended" never wrap to a
   second line; `display: inline-flex` keeps the pill height stable. */
.ref-card-status-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 11px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    border: 1px solid currentColor;
    white-space: nowrap;
    line-height: 1.3;
}
.ref-card-status-pill.is-active    { color: #065f46; background: #d1fae5; border-color: #6ee7b7; }
.ref-card-status-pill.is-inactive  { color: #475569; background: #e2e8f0; border-color: #cbd5e1; }
.ref-card-status-pill.is-suspended { color: #991b1b; background: #fee2e2; border-color: #fca5a5; }
.ref-card-status-pill.is-diaspora  { color: #6d28d9; background: #ede9fe; border-color: #c4b5fd; }

/* Footer — paired marks left, ownership note right. */
.ref-card-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    padding: 8px 14px;
    background: #022C22;
    color: rgba(220, 252, 231, 0.75);
    font-size: 10.5px;
    letter-spacing: 0.05em;
}
/* Paired federation marks — FRVB on the left, ARAVO on the right.
   Both are boxed so they read as official seals on the card. */
.ref-card-marks { display: inline-flex; gap: 8px; align-items: center; flex-shrink: 0; }
.ref-card-mark {
    display: inline-flex; align-items: center; justify-content: center;
    font-family: "Manrope", "Inter", sans-serif;
    font-weight: 800;
    font-size: 11px;
    letter-spacing: 0.18em;
    padding: 4px 10px;
    border-radius: 5px;
    line-height: 1;
}
.ref-card-mark-frvb {
    background: #ffffff;
    color: #022C22;
    border: 1px solid #FACC15;
    box-shadow: inset 0 0 0 2px rgba(250, 204, 21, 0.18);
}
.ref-card-mark-aravo {
    background: linear-gradient(135deg, #FACC15 0%, #EAB308 100%);
    color: #022C22;
    border: 1px solid rgba(202, 138, 4, 0.6);
}
.ref-card-foot-note {
    color: rgba(220, 252, 231, 0.85);
    font-style: italic;
    font-size: 10.5px;
    line-height: 1.3;
    text-align: right;
    flex: 1 1 auto;
    min-width: 0;
}

/* While the JPEG export is rasterising the card, we:
     · REMOVE toolbar controls so they don't appear in the saved image
       AND don't reserve space — display:none frees the header width so
       the federation title can use the full card and stay on one line
       (visibility:hidden left a gap that pushed "Volleyball" to wrap),
     · strip the drop shadow so its halo isn't clipped at the
       captured canvas's edges,
     · neutralise transforms so any parent zoom utility doesn't
       shrink the captured node.
   `data-no-export` is belt-and-braces alongside the `filter`
   callback we pass to html-to-image. */
.ref-card.is-exporting [data-no-export] { display: none !important; }
.ref-card.is-exporting {
    box-shadow: none !important;
    transform: none !important;
    /* When exporting we want the saved JPEG to look identical no
       matter what screen took the screenshot — force the desktop
       width + two-column body so mobile users get the same card
       a laptop user would. The width here roughly matches the
       max-width above. */
    width: 560px !important;
    max-width: 560px !important;
}
.ref-card.is-exporting .ref-card-body {
    grid-template-columns: 154px 1fr !important;
}

/* Visually de-emphasize the whole card when the referee isn't
   currently active. Status pill + watermark already shout the state. */
.ref-card[data-status="inactive"] .ref-card-photo-img,
.ref-card[data-status="suspended"] .ref-card-photo-img { filter: grayscale(1) brightness(0.95); }
.ref-card[data-status="inactive"]  .ref-card-head { background: linear-gradient(120deg, #475569 0%, #334155 100%); }
.ref-card[data-status="suspended"] .ref-card-head { background: linear-gradient(120deg, #7f1d1d 0%, #991b1b 100%); }

/* Print: shoulder-tighter, no shadow, watermark still readable. */
@media print {
    .ref-card {
        box-shadow: none !important;
        border: 0.7pt solid #94a3b8 !important;
        max-width: none;
        page-break-inside: avoid;
    }
    .ref-card-dl { display: none !important; }
    .ref-card-photo-meta { display: none !important; }
    .ref-card-photo-overlay { display: none !important; }
    .ref-card-head { padding: 8pt 12pt; }
    .ref-card-body { padding: 10pt 12pt; }
    .ref-card-foot { padding: 5pt 10pt; }
    .ref-card-stamp { opacity: 0.25 !important; }
}
</style>
